/**
 * The front door.
 *
 * Shown instead of the app whenever nobody is signed in. Sprout waits here,
 * because an empty box asking for a password is a miserable way to be greeted
 * by something that is meant to be a bit of fun.
 *
 * The button is a real form rather than anything clever on purpose: going to
 * Google has to be an actual page change, not a quiet request in the
 * background, or Google's sign-in page has nowhere to appear.
 */
import { Creature } from "../components/Creature";

/**
 * What to say when a sign-in did not work out. The reason arrives in the
 * address as ?signin=... after Google sends the browser back.
 *
 * None of these blame Sam, and none of them mention anything she cannot act
 * on. The details are in the server log, where they are useful.
 */
const REASONS: Record<string, string> = {
  denied: "No problem — sign in whenever you're ready.",
  expired: "That took a bit too long, so it started over. Try again?",
  not_allowed: "That Google account isn't on the list. Try the other one?",
  failed: "Something went wrong on the way to Google. Worth another go.",
  config: "Sign-in isn't finished being set up yet. Ted needs to have a look.",
};

export function SignIn({ configured }: { configured: boolean }) {
  // Read straight from the address rather than kept in state: this screen is
  // reached by Google sending the browser here, so the address is the only
  // place the reason could have come from.
  const query = new URLSearchParams(window.location.search);
  const reason = query.get("signin");
  const message = reason ? REASONS[reason] ?? REASONS.failed : null;

  // Come back to whatever she was trying to open, so a link to a particular
  // screen still works after signing in. Without the reason for the last
  // failure, which would otherwise be carried along and sit in the address
  // bar after a sign-in that worked.
  query.delete("signin");
  const rest = query.toString();
  const next = window.location.pathname + (rest ? `?${rest}` : "");

  return (
    <main className="app signin">
      <div className="signin__creature">
        <Creature mood="waiting" name="Sprout" />
      </div>

      <h1 className="signin__title">Sam's Fitness Pal</h1>
      <p className="signin__blurb">Sprout's been waiting for you.</p>

      {message && (
        <p className={`signin__note${reason === "not_allowed" ? " signin__note--warn" : ""}`}>
          {message}
        </p>
      )}

      {configured ? (
        <form method="post" action="/api/auth/start" className="signin__form">
          <input type="hidden" name="next" value={next} />
          <button type="submit" className="button button--full">
            Sign in with Google
          </button>
        </form>
      ) : (
        <p className="signin__note signin__note--warn">
          Sign-in isn't set up on the server yet, so there's nothing to sign in to. Ted needs to
          have a look.
        </p>
      )}
    </main>
  );
}
